import React, { useState, useRef, useEffect } from 'react';
import './FecFileInput.css';
import Warning from '../../../atoms/icons/general/warning/Warning';
import Trash from '../../../atoms/icons/general/trash/Trash';
import Pen from '../../../atoms/icons/monCompte/pen/Pen';
import FilledButton from '../../buttons/filledButton/FilledButton';

function FecFileInput({
  label = 'Fichier FEC (.txt, .csv ou .fec)',
  name = 'fec_file',
  register,
  setValue = () => {},
  bgColor = 'var(--main-color)',
  color = 'var(--white)',
  hoverColor = 'var(--main-color)',
  borderRadius = '10px',
  padding = '15px 20px',
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onClear = () => {},
  onProcess = () => {},
  disabled,
  visible,
  buttonText = 'Importer un fichier FEC',
  className = '',
  fileMaxSize = 35, // en MB
  isProcessing = false,
  isNewEstimation = false,
  forceReset = false,
}) {
  const [fecErrorMessage, setFecErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const inputRegister = register && (visible || visible === undefined) ? register(name) : null;

  useEffect(() => {
    if (visible !== undefined) {
      inputRef.current.style.display = visible ? 'block' : 'none';
    }
  }, [visible]);

  useEffect(() => {
    if (forceReset) {
      setSelectedFile(null);
      setFilePreview(null);
      setFecErrorMessage('');
      setValue(name, null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [forceReset, name, setValue]);

  function checkFecFile(file) {
    if (!file) return 'Aucun fichier sélectionné';

    const allowedTypes = ['text/plain', 'text/csv', 'application/csv'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !['txt', 'csv', 'fec'].includes(fileExtension)) {
      return 'Format de fichier non supporté. Veuillez sélectionner un fichier .txt, .csv ou .fec';
    }

    const maxSizeInBytes = fileMaxSize * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return `Le fichier est trop volumineux. Taille maximale autorisée : ${fileMaxSize}MB`;
    }

    return null;
  }

  function openDB() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) return reject(new Error('IndexedDB non disponible'));
      const req = window.indexedDB.open('cside-fec-files', 1);
      req.onupgradeneeded = () => {
        try {
          req.result.createObjectStore('files');
        } catch (e) {}
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = e => reject(e.target?.error || e);
    });
  }

  async function saveFileToDB(key, value) {
    try {
      const db = await openDB();
      const tx = db.transaction('files', 'readwrite');
      tx.objectStore('files').put(value, key);
      return new Promise((res, rej) => {
        tx.oncomplete = () => res();
        tx.onerror = () => rej(tx.error);
      });
    } catch (e) {
      console.warn('saveFileToDB failed', e);
    }
  }

  async function getFileFromDB(key) {
    try {
      const db = await openDB();
      const tx = db.transaction('files', 'readonly');
      const req = tx.objectStore('files').get(key);
      return await new Promise((res, rej) => {
        req.onsuccess = () => res(req.result);
        req.onerror = () => rej(req.error);
      });
    } catch (e) {
      console.warn('getFileFromDB failed', e);
      return null;
    }
  }

  async function deleteFileFromDB(key) {
    try {
      const db = await openDB();
      const tx = db.transaction('files', 'readwrite');
      tx.objectStore('files').delete(key);
      return new Promise((res, rej) => {
        tx.oncomplete = () => res();
        tx.onerror = () => rej(tx.error);
      });
    } catch (e) {
      console.warn('deleteFileFromDB failed', e);
    }
  }

  async function readFilePreview(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target.result;
        const allLines = content.split('\n').filter(line => line.trim());
        const previewLines = allLines.slice(0, 10);

        let separator = '\t';
        if (previewLines.length > 0) {
          const firstLine = previewLines[0];
          if (firstLine.includes('|')) separator = '|';
          else if (firstLine.includes(';')) separator = ';';
          else if (firstLine.includes('\t')) separator = '\t';
        }

        const fecColumns = [
          'JournalCode',
          'JournalLib',
          'EcritureNum',
          'EcritureDate',
          'CompteNum',
          'CompteLib',
          'CompAuxNum',
          'CompAuxLib',
          'PieceRef',
          'PieceDate',
          'EcritureLib',
          'Debit',
          'Credit',
          'EcritureLet',
          'DateLet',
          'ValidDate',
          'Montantdevise',
          'Idevise',
        ];

        const tableData = previewLines.map((line, index) => {
          const cells = line.split(separator).map(cell => cell.trim());

          if (index === 0) {
            return fecColumns;
          }

          const normalizedCells = [];
          for (let i = 0; i < 18; i++) {
            normalizedCells[i] = cells[i] || '';
          }

          return normalizedCells;
        });

        if (tableData.length > 0 && !tableData[0].includes('JournalCode')) {
          tableData.unshift(fecColumns);
        }

        resolve({
          name: file.name,
          size: (file.size / 1024).toFixed(2) + ' KB',
          lines: previewLines.length,
          totalLines: allLines.length,
          preview: previewLines.join('\n'),
          tableData: tableData,
          separator: separator,
          type: file.name.split('.').pop().toUpperCase(),
        });
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  async function handleChange(e) {
    const file = e.target.files[0];

    if (!file) {
      setIsLoading(false);
      return;
    }

    await processFile(file);
  }

  async function processFile(file) {
    const errorMsg = checkFecFile(file);

    if (errorMsg) {
      setFecErrorMessage(errorMsg);
      setSelectedFile(null);
      setFilePreview(null);
      return;
    }

    setIsLoading(true);
    try {
      const preview = await readFilePreview(file);

      setSelectedFile(file);
      setFilePreview(preview);
      setValue(name, file);

      try {
        await saveFileToDB(name, file);
        await saveFileToDB(name + '_preview', preview);
      } catch (e) {
        console.warn('Erreur persistance FEC:', e);
      }
      setFecErrorMessage('');

      onChange(file, preview);
    } catch (error) {
      setFecErrorMessage('Erreur lors de la lecture du fichier');
      console.error('Erreur lecture fichier FEC:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function replaceFile() {
    deleteFile();
    triggerInput();
  }

  function deleteFile() {
    setSelectedFile(null);
    setFilePreview(null);
    setValue(name, null);
    setFecErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    try {
      deleteFileFromDB(name);
      deleteFileFromDB(name + '_preview');
    } catch (e) {
      console.warn('Erreur suppression FEC en base:', e);
    }
    onClear();
  }

  function triggerInput() {
    setIsLoading(true);
    if (fileInputRef.current) {
      fileInputRef.current.click();

      const handleFocus = () => {
        setTimeout(() => {
          if (!fileInputRef.current?.files?.length) {
            setIsLoading(false);
          }
        }, 100);

        window.removeEventListener('focus', handleFocus);
      };

      window.addEventListener('focus', handleFocus);
    }
  }

  function handleProcessFile() {
    if (selectedFile && filePreview) {
      onProcess(selectedFile, filePreview);
    }
  }

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }

  useEffect(() => {
    if (isNewEstimation || forceReset) {
      try {
        deleteFileFromDB(name);
        deleteFileFromDB(name + '_preview');
      } catch (e) {}
      return;
    }

    let mounted = true;
    async function restore() {
      try {
        const saved = await getFileFromDB(name);
        if (!mounted || !saved) return;
        const savedPreview = await getFileFromDB(name + '_preview');
        setSelectedFile(saved);
        setFilePreview(savedPreview || null);
        setValue(name, saved);
      } catch (e) {}
    }
    restore();
    return () => {
      mounted = false;
    };
  }, [isNewEstimation, forceReset, name]);

  return (
    <div
      className={`fec-file-input-container ${className} ${isDragActive ? 'drag-active' : ''}`}
      ref={inputRef}
      style={{
        '--padding': padding,
        '--background-color': bgColor,
        '--color': color,
        '--hover-color': hoverColor,
        '--border-radius': borderRadius,
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      {label && <label className='form-label'>{label}</label>}

      <div className='fec-file-input-content' ref={inputContainerRef}>
        <div className={`fec-file-preview ${selectedFile ? '' : 'd-none'}`}>
          <div className='fec-file-info-header'>
            <div className='fec-file-meta'>
              <h4>📄 {filePreview?.name}</h4>
              <p className='text-sm'>
                {filePreview?.type} • {filePreview?.size} • {filePreview?.totalLines} lignes
              </p>
            </div>
            <div className='fec-file-actions'>
              <button type='button' className='fec-file-action-btn' onClick={replaceFile} title='Remplacer le fichier'>
                <Pen color='white' width='16px' />
              </button>
              <button type='button' className='fec-file-action-btn' onClick={deleteFile} title='Supprimer le fichier'>
                <Trash color='white' width='16px' />
              </button>
            </div>
          </div>

          <div className='fec-file-preview-content'>
            <h5>Aperçu du contenu :</h5>
            <div className='fec-table-container'>
              {filePreview?.tableData && filePreview.tableData.length > 0 ? (
                <table className='fec-preview-table'>
                  <tbody>
                    {filePreview.tableData.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'fec-header-row' : ''}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className='fec-table-cell'>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <pre className='fec-content-preview'>{filePreview?.preview}</pre>
              )}
              {filePreview?.totalLines > 10 && (
                <div className='preview-truncated'>... et {filePreview.totalLines - 10} lignes supplémentaires</div>
              )}
            </div>
          </div>

          <div className='fec-file-preview-buttons'>
            <FilledButton
              onClick={handleProcessFile}
              disabled={isProcessing}
              isLoading={isProcessing}
              className='fec-process-button'>
              {isProcessing ? 'Traitement en cours...' : 'Valider le fichier FEC'}
            </FilledButton>
          </div>
        </div>

        <div className={selectedFile ? 'd-none' : 'fec-upload-zone'}>
          <div className='fec-upload-content'>
            <div className='fec-upload-icon'>📄</div>
            <p className='fec-upload-text'>Glissez-déposez votre fichier FEC ici ou</p>
            <FilledButton isLoading={isLoading} onClick={triggerInput} className='fec-upload-button'>
              {buttonText}
            </FilledButton>
          </div>

          <input
            ref={fileInputRef}
            type='file'
            className='fec-file-input-hidden'
            onChange={handleChange}
            disabled={(visible !== undefined && !visible) || disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            accept='.txt,.csv,.fec'
          />
          <input type='hidden' id={name} {...inputRegister} />
        </div>
      </div>

      {fecErrorMessage && (
        <div className='form-error'>
          <Warning /> <p>{fecErrorMessage}</p>
        </div>
      )}
    </div>
  );
}

export default FecFileInput;
